import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';
import ChatListPanel from '../components/Chat/ChatListPanel';
import ChatDetailPanel from '../components/Chat/ChatDetailPanel';
import ChatProfileSidebar from '../components/Chat/ChatProfileSidebar';
import { getColors, typography, spacing } from '../styles/theme';
import { useTheme } from '../contexts/ThemeContext';

const ChatContainer = ({ navigation, route }) => {
  const { width } = useWindowDimensions();
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const isWeb = Platform.OS === 'web';
  const isDesktop = isWeb && width >= 1024; // Desktop breakpoint

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setSelectedUser(conversation.otherUser);
  };

  const handleBack = () => {
    setSelectedConversation(null);
    setSelectedUser(null);
  };

  const styles = getStyles(colors, isDesktop);
  
  if (isDesktop) {
    // Desktop: 3-column layout
    return (
      <View style={styles.desktopContainer}>
        {/* Left: Chat List */}
        <View style={styles.leftPanel}>
          <ChatListPanel
            navigation={navigation}
            onSelectConversation={handleSelectConversation}
            selectedConversationId={selectedConversation?.id}
            isMobile={false}
            openUserId={route.params?.openUserId}
            openUser={route.params?.openUser}
          />
        </View>

        {/* Middle: Chat Detail */}
        <View style={styles.middlePanel}>
          {selectedConversation ? (
            <ChatDetailPanel
              conversation={selectedConversation}
              otherUser={selectedUser}
              navigation={navigation}
            />
          ) : (
            <View style={styles.emptyState}>
              <Icon name="chat-bubble-outline" size={64} color={colors.textLight} />
              <Text style={styles.emptyStateText}>Select a conversation to start chatting</Text>
            </View>
          )}
        </View>

        {/* Right: Profile Sidebar */}
        <View style={styles.rightPanel}>
          {selectedUser ? (
            <ChatProfileSidebar user={selectedUser} navigation={navigation} />
          ) : (
            <View style={styles.emptyState}>
              <Icon name="person-outline" size={48} color={colors.textLight} />
              <Text style={styles.emptyStateText}>Select a chat to view profile</Text>
            </View>
          )}
        </View>
      </View>
    );
  }

  // Mobile: Show list or detail based on selection
  if (selectedConversation) {
    // Mobile: Show ChatDetailPanel in full-screen mode
    return (
      <View style={styles.mobileContainer}>
        <ChatDetailPanel
          conversation={selectedConversation}
          otherUser={selectedUser}
          navigation={navigation}
          isMobile={true}
          onBack={handleBack}
        />
      </View>
    );
  }

  // Mobile: Show chat list
  return (
    <ChatListPanel
      navigation={navigation}
      onSelectConversation={handleSelectConversation}
      isMobile={true}
      openUserId={route.params?.openUserId}
      openUser={route.params?.openUser}
    />
  );
};

// Dynamic styles based on theme
const getStyles = (colors, isDesktop) => StyleSheet.create({
  desktopContainer: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: colors.background,
  },
  mobileContainer: {
    flex: 1,
    backgroundColor: colors.background,
  },
  leftPanel: {
    width: 420,
    borderRightWidth: 1,
    borderRightColor: colors.border,
    backgroundColor: colors.surface,
  },
  middlePanel: {
    flex: 1,
    backgroundColor: colors.background,
  },
  rightPanel: {
    width: 432,
    borderLeftWidth: 1,
    borderLeftColor: colors.border,
    backgroundColor: colors.surface,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyStateText: {
    marginTop: 16,
    fontSize: 16,
    color: colors.textSecondary,
    textAlign: 'center',
  },
});

export default ChatContainer;

